// filepath: apps/web/src/editors/bpm-designer/BpmCanvas.tsx
//
// FR-UI-BLD-1 AC-3 - center canvas. SVG canvas with foreignObject
// rendered node boxes + line-based edge rendering. Click to select.
// Drag a node to reposition (state-tracked). Connect from output
// handle to input handle via drag-and-drop.

import React from "react";
import { useTranslation } from "react-i18next";
import type {
  EdgeDefinition,
  NodeDefinition,
  ValidationErrorWire,
} from "./types";
import { InlineNodeError } from "./InlineNodeError";

interface BpmCanvasProps {
  nodes: NodeDefinition[];
  edges: EdgeDefinition[];
  selectedNodeId?: string;
  selectedEdgeId?: string;
  onSelectNode: (nodeId: string) => void;
  onSelectEdge: (edgeId: string) => void;
  onRemoveNode: (nodeId: string) => void;
  onMoveNode: (nodeId: string, x: number, y: number) => void;
  onConnect: (sourceId: string, targetId: string) => void;
  onDropNodeType: (nodeType: string) => void;
  errors: ValidationErrorWire[];
}

interface LocalPos {
  x: number;
  y: number;
}

const NODE_BOX_W = 120;
const NODE_BOX_H = 56;
const DEFAULT_COL_GAP = 60;
const DEFAULT_ROW_GAP = 32;

function defaultLayout(nodes: NodeDefinition[]): Record<string, LocalPos> {
  const positions: Record<string, LocalPos> = {};
  nodes.forEach((n, idx) => {
    const col = idx % 4;
    const row = Math.floor(idx / 4);
    positions[n.node_id] = {
      x: 32 + col * (NODE_BOX_W + DEFAULT_COL_GAP),
      y: 32 + row * (NODE_BOX_H + DEFAULT_ROW_GAP),
    };
  });
  return positions;
}

export function BpmCanvas({
  nodes,
  edges,
  selectedNodeId,
  selectedEdgeId,
  onSelectNode,
  onSelectEdge,
  onRemoveNode,
  onMoveNode,
  onConnect,
  onDropNodeType,
  errors,
}: BpmCanvasProps): React.ReactElement {
  const { t } = useTranslation();
  const positions = defaultLayout(nodes);
  const [draggingSourceId, setDraggingSourceId] = React.useState<
    string | undefined
  >();

  return (
    <section
      className="bpm-canvas"
      data-testid="bpm-canvas"
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        const nt = e.dataTransfer.getData("nodeType");
        if (nt) {
          onDropNodeType(nt);
        }
      }}
    >
      <h2 className="bpm-canvas__title">{t("admin.designer.canvas.title")}</h2>
      {nodes.length === 0 ? (
        <p className="bpm-canvas__empty">{t("admin.designer.canvas.empty")}</p>
      ) : (
        <svg
          className="bpm-canvas__svg"
          role="img"
          aria-label={t("admin.designer.canvas.aria")}
          data-testid="bpm-canvas-svg"
          width="100%"
          height={Math.max(
            240,
            32 + Math.ceil(nodes.length / 4) * (NODE_BOX_H + DEFAULT_ROW_GAP),
          )}
          viewBox={`0 0 800 ${Math.max(
            240,
            32 + Math.ceil(nodes.length / 4) * (NODE_BOX_H + DEFAULT_ROW_GAP),
          )}`}
        >
          <g className="bpm-canvas__edges">
            {edges.map((e) => {
              const src = positions[e.source_node_id];
              const dst = positions[e.target_node_id];
              if (!src || !dst) return null;
              const x1 = src.x + NODE_BOX_W;
              const y1 = src.y + NODE_BOX_H / 2;
              const x2 = dst.x;
              const y2 = dst.y + NODE_BOX_H / 2;
              const isSelected = selectedEdgeId === e.edge_id;
              return (
                <line
                  key={e.edge_id}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={isSelected ? "#1976d2" : "#888"}
                  strokeWidth={isSelected ? 2.5 : 1.5}
                  onClick={() => onSelectEdge(e.edge_id)}
                  data-testid={`canvas-edge-${e.edge_id}`}
                  data-edge-id={e.edge_id}
                />
              );
            })}
          </g>
          <g className="bpm-canvas__nodes">
            {nodes.map((n) => {
              const pos = positions[n.node_id];
              if (!pos) return null;
              const isSelected = selectedNodeId === n.node_id;
              return (
                <foreignObject
                  key={n.node_id}
                  x={pos.x}
                  y={pos.y}
                  width={NODE_BOX_W}
                  height={NODE_BOX_H}
                >
                  <div
                    className={`bpm-canvas__node ${
                      isSelected ? "is-selected" : ""
                    }`}
                    data-node-id={n.node_id}
                    data-testid={`canvas-node-${n.node_id}`}
                    onClick={() => onSelectNode(n.node_id)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("nodeMove", n.node_id);
                      setDraggingSourceId(n.node_id);
                    }}
                    onDragEnd={() => {
                      setDraggingSourceId(undefined);
                      onMoveNode(n.node_id, pos.x, pos.y);
                    }}
                  >
                    <header className="bpm-canvas__node-header">
                      <strong>{n.node_id}</strong>
                      <button
                        type="button"
                        className="bpm-canvas__node-remove"
                        onClick={(e2) => {
                          e2.stopPropagation();
                          onRemoveNode(n.node_id);
                        }}
                        aria-label={t("admin.designer.canvas.remove", {
                          id: n.node_id,
                        })}
                      >
                        X
                      </button>
                    </header>
                    <div className="bpm-canvas__node-type">{n.node_type}</div>
                    <span
                      className="bpm-canvas__node-handle bpm-canvas__node-handle--output"
                      data-testid={`canvas-node-handle-out-${n.node_id}`}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("edgeSource", n.node_id);
                        setDraggingSourceId(n.node_id);
                      }}
                    />
                    <span
                      className="bpm-canvas__node-handle bpm-canvas__node-handle--input"
                      data-testid={`canvas-node-handle-in-${n.node_id}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = e.dataTransfer.getData("edgeSource");
                        if (src && src !== n.node_id) {
                          onConnect(src, n.node_id);
                        }
                      }}
                    />
                    <InlineNodeError nodeId={n.node_id} errors={errors} />
                  </div>
                </foreignObject>
              );
            })}
          </g>
        </svg>
      )}
      {draggingSourceId && (
        <span data-testid="canvas-dragging-from" className="sr-only">
          {t("admin.designer.canvas.dragging", { id: draggingSourceId })}
        </span>
      )}
    </section>
  );
}
